/**
 * @typedef {GetDateOffsetArgs}
 * @property {string} rangeValue The value of the datetime search range
 * @property {string} rangeUnit The unit of the datetime search range
 * @property {Date} rangeEnd The last date in the filter range; getDateOffset calculates the start date using rangeValue/rangeUnit
 */
interface GetDateOffsetArgs {
    rangeValue: string;
    rangeUnit: string;
    rangeEnd: Date;
}

/**
 * Given an offset and end date, creates a JavaScript date object representing the beginning of a date filter
 * @param   {GetDateOffsetArgs} The unit/value of the search range, and the latest date allowed by the filter
 * @returns {Date} A JavaScript date representing the earliest date allowed by the filter
 */
export const getEarliestDateFromOffset = ({
    rangeValue,
    rangeUnit,
    rangeEnd,
}: GetDateOffsetArgs): Date => {
    const rangeStart = new Date(rangeEnd.getTime());
    const offset = parseInt(rangeValue, 10);

    switch (rangeUnit) {
        case 'years':
            rangeStart.setUTCFullYear(rangeStart.getUTCFullYear() - offset);
            break;
        case 'months':
            rangeStart.setUTCMonth(rangeStart.getUTCMonth() - offset);
            break;
        case 'weeks':
            rangeStart.setUTCDate(rangeStart.getUTCDate() - offset * 7);
            break;
        case 'days':
            rangeStart.setUTCDate(rangeStart.getUTCDate() - offset);
            break;
        case 'hours':
            rangeStart.setUTCHours(rangeStart.getUTCHours() - offset);
            break;
        case 'minutes':
            rangeStart.setUTCMinutes(rangeStart.getUTCMinutes() - offset);
            break;
        case 'seconds':
            rangeStart.setUTCSeconds(rangeStart.getUTCSeconds() - offset);
            break;
        default:
            break;
    }

    return rangeStart;
};
